import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const TEMPLATE_PATH = `${process.env.PUBLIC_URL || ""}/templates/Direct_Deposit_Form_Template.pdf`;
const PAGE_HEIGHT = 792;

// Text baselines/x derived from the actual blank lines in the official template.
// maxWidth limits a field's slot so long values shrink instead of overlapping labels.
const LAYOUT = {
  name: { x: 192, y: 640, maxWidth: 248 },
  tribalId: { x: 494, y: 640, maxWidth: 44 },
  address: { x: 124, y: 620, maxWidth: 148 },
  city: { x: 300, y: 620, maxWidth: 86 },
  state: { x: 424, y: 620 },
  zipCode: { x: 490, y: 620, maxWidth: 48 },
  phone: { x: 120, y: 600, maxWidth: 135 },
  accounts: [
    {
      bankName: { x: 206, y: 560, maxWidth: 330 },
      amount: { x: 212, y: 518, maxWidth: 95 },
      percentage: { x: 377, y: 518, maxWidth: 34 },
      routing: { x: 210, y: 498, maxWidth: 95 },
      accountNumber: { x: 410, y: 498, maxWidth: 125 },
    },
    {
      bankName: { x: 210, y: 455, maxWidth: 326 },
      amount: { x: 212, y: 414, maxWidth: 95 },
      percentage: { x: 377, y: 414, maxWidth: 34 },
      routing: { x: 210, y: 394, maxWidth: 95 },
      accountNumber: { x: 410, y: 394, maxWidth: 125 },
    },
    {
      bankName: { x: 210, y: 350, maxWidth: 326 },
      amount: { x: 212, y: 309, maxWidth: 95 },
      percentage: { x: 377, y: 309, maxWidth: 34 },
      routing: { x: 210, y: 289, maxWidth: 95 },
      accountNumber: { x: 410, y: 289, maxWidth: 125 },
    },
  ],
  printName: { x: 80, y: 210, maxWidth: 110 },
  authorizeDate: { x: 415, y: 206, maxWidth: 95 },
  signatureDate: { x: 445, y: 173, maxWidth: 70 },
  signatureImage: { x: 150, y: 138, width: 110, height: 26 },
};

// Checkbox squares detected from template (top-left origin): { x0, topY, size }.
const CHECKBOXES = {
  paymentType: {
    employee: { x0: 230.2, topY: 103, size: 14 },
    accountsPayable: { x0: 310.5, topY: 102, size: 14 },
    perCapita: { x0: 229.8, topY: 115, size: 14 },
    education: { x0: 310.2, topY: 116, size: 14 },
  },
  accounts: [
    {
      checking: { x0: 179.5, topY: 240.7, size: 14.5 },
      savings: { x0: 293.0, topY: 240.2, size: 14.5 },
    },
    {
      checking: { x0: 178.7, topY: 346.5, size: 14 },
      savings: { x0: 292.7, topY: 346.3, size: 14 },
    },
    {
      checking: { x0: 178.3, topY: 450.3, size: 14 },
      savings: { x0: 291.0, topY: 450.7, size: 14 },
    },
  ],
};

const normalize = (value) => (value ?? "").toString().trim().toLowerCase();

const createPdfAdapter = (bytes) => ({
  output: (type) => {
    if (type === "blob") {
      return new Blob([bytes], { type: "application/pdf" });
    }
    if (type === "arraybuffer") {
      return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
    }
    throw new Error(`Unsupported PDF output type: ${type}`);
  },
  save: (filename) => {
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
});

const getIndexedFieldValue = (snapshot, displayName, index = 0) => {
  const metaList = snapshot.fieldsMeta || [];
  const formObj = snapshot.formData || {};
  const target = normalize(displayName);
  const matches = metaList.filter((field) => normalize(field.displayname) === target);

  if (!matches[index]) return "";
  const value = formObj[matches[index].fieldname];
  return value != null && String(value).trim() !== "" ? String(value).trim() : "";
};

const getFieldValueByMatch = (snapshot, matcher) => {
  const metaList = snapshot.fieldsMeta || [];
  const formObj = snapshot.formData || {};

  for (const field of metaList) {
    if (!matcher(normalize(field.displayname))) continue;
    const value = formObj[field.fieldname];
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }

  return "";
};

const getBankName = (snapshot, accountIndex) => {
  const ordinals = ["1st", "2nd", "3rd"];
  const ordinal = ordinals[accountIndex];
  const byOrdinal = getFieldValueByMatch(
    snapshot,
    (name) => name.includes("bank name") && name.includes(ordinal)
  );
  if (byOrdinal) return byOrdinal;

  return getIndexedFieldValue(snapshot, "Bank Name", accountIndex);
};

const collectPaymentTypes = (snapshot) => {
  const raw =
    getIndexedFieldValue(snapshot, "Payment Type") ||
    getFieldValueByMatch(snapshot, (name) => name.includes("payment type"));

  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map((item) => normalize(item));
  }

  return raw
    .split(/[,;|]/)
    .map((item) => normalize(item))
    .filter(Boolean);
};

const paymentTypeSelected = (selectedTypes, ...options) =>
  options.some((option) =>
    selectedTypes.some(
      (selected) => selected === option || selected.includes(option) || option.includes(selected)
    )
  );

const drawFieldText = (page, text, position, font, size = 10) => {
  if (!text) return;
  const value = String(text);

  // Shrink font so the value fits within its slot instead of overlapping nearby labels.
  let fontSize = size;
  if (position.maxWidth) {
    while (fontSize > 6 && font.widthOfTextAtSize(value, fontSize) > position.maxWidth) {
      fontSize -= 0.5;
    }
  }

  page.drawText(value, {
    x: position.x,
    y: position.y,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  });
};

const drawCheckboxMark = (page, box, checked) => {
  if (!checked || !box) return;

  const { x0, topY, size } = box;
  const pdfTop = PAGE_HEIGHT - topY;
  const pdfBottom = pdfTop - size;
  const inset = 2.5;
  const color = rgb(0, 0, 0);

  page.drawLine({
    start: { x: x0 + inset, y: pdfBottom + inset },
    end: { x: x0 + size - inset, y: pdfTop - inset },
    thickness: 1,
    color,
  });
  page.drawLine({
    start: { x: x0 + size - inset, y: pdfBottom + inset },
    end: { x: x0 + inset, y: pdfTop - inset },
    thickness: 1,
    color,
  });
};

const formatPdfDate = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${month}/${day}/${year}`;
  }
  return trimmed;
};

const dataUrlToUint8Array = async (dataUrl) => {
  const response = await fetch(dataUrl);
  const buffer = await response.arrayBuffer();
  return new Uint8Array(buffer);
};

export async function generateDirectDepositPdf(snapshot) {
  const templateResponse = await fetch(TEMPLATE_PATH);
  if (!templateResponse.ok) {
    throw new Error("Failed to load direct deposit PDF template");
  }

  const templateBytes = await templateResponse.arrayBuffer();
  const pdfDoc = await PDFDocument.load(templateBytes);
  const page = pdfDoc.getPages()[0];
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const paymentTypes = collectPaymentTypes(snapshot);
  const name = getIndexedFieldValue(snapshot, "Name/Business Name");
  const tribalId = getIndexedFieldValue(snapshot, "Tribal ID");
  const address = getIndexedFieldValue(snapshot, "Address");
  const city = getIndexedFieldValue(snapshot, "City");
  const state = getIndexedFieldValue(snapshot, "State");
  const zipCode =
    getIndexedFieldValue(snapshot, "Zip Code") ||
    getIndexedFieldValue(snapshot, "Zipcode") ||
    getIndexedFieldValue(snapshot, "ZIPCODE");
  const phone =
    getIndexedFieldValue(snapshot, "Phone#") ||
    getIndexedFieldValue(snapshot, "Phone") ||
    getFieldValueByMatch(snapshot, (fieldName) => fieldName.includes("phone"));

  drawCheckboxMark(page, CHECKBOXES.paymentType.employee, paymentTypeSelected(paymentTypes, "employee"));
  drawCheckboxMark(
    page,
    CHECKBOXES.paymentType.accountsPayable,
    paymentTypeSelected(paymentTypes, "accounts payable")
  );
  drawCheckboxMark(page, CHECKBOXES.paymentType.perCapita, paymentTypeSelected(paymentTypes, "per capita"));
  drawCheckboxMark(
    page,
    CHECKBOXES.paymentType.education,
    paymentTypeSelected(paymentTypes, "education/reimbursement", "education", "reimbursement")
  );

  drawFieldText(page, name, LAYOUT.name, font);
  drawFieldText(page, tribalId, LAYOUT.tribalId, font);
  drawFieldText(page, address, LAYOUT.address, font);
  drawFieldText(page, city, LAYOUT.city, font);
  drawFieldText(page, state, LAYOUT.state, font);
  drawFieldText(page, zipCode, LAYOUT.zipCode, font);
  drawFieldText(page, phone, LAYOUT.phone, font);

  LAYOUT.accounts.forEach((accountLayout, index) => {
    const accountType = normalize(getIndexedFieldValue(snapshot, "Account Type", index));
    const amount = getIndexedFieldValue(snapshot, "Amount", index);
    const percentage = getIndexedFieldValue(snapshot, "Percentage", index);
    const routingNumber = getIndexedFieldValue(snapshot, "Routing Number", index);
    const accountNumber = getIndexedFieldValue(snapshot, "Account Number", index);
    const bankName = getBankName(snapshot, index);
    const checkboxLayout = CHECKBOXES.accounts[index];

    drawFieldText(page, bankName, accountLayout.bankName, font);
    drawCheckboxMark(page, checkboxLayout.checking, accountType.includes("checking"));
    drawCheckboxMark(page, checkboxLayout.savings, accountType.includes("saving"));
    drawFieldText(page, amount, accountLayout.amount, font);
    drawFieldText(page, percentage, accountLayout.percentage, font);
    drawFieldText(page, routingNumber, accountLayout.routing, font);
    drawFieldText(page, accountNumber, accountLayout.accountNumber, font);
  });

  const signatureDate =
    formatPdfDate(snapshot.signatureDate) ||
    formatPdfDate(getIndexedFieldValue(snapshot, "Date")) ||
    formatPdfDate(getFieldValueByMatch(snapshot, (fieldName) => fieldName.includes("date")));

  drawFieldText(page, name, LAYOUT.printName, font);
  drawFieldText(page, signatureDate, LAYOUT.authorizeDate, font);
  drawFieldText(page, signatureDate, LAYOUT.signatureDate, font);

  if (snapshot.signatureDataUrl) {
    try {
      const signatureBytes = await dataUrlToUint8Array(snapshot.signatureDataUrl);
      const isPng = snapshot.signatureDataUrl.includes("image/png");
      const signatureImage = isPng
        ? await pdfDoc.embedPng(signatureBytes)
        : await pdfDoc.embedJpg(signatureBytes);

      page.drawImage(signatureImage, LAYOUT.signatureImage);
    } catch (error) {
      console.error("Failed to embed direct deposit signature:", error);
    }
  }

  const bytes = await pdfDoc.save();
  return createPdfAdapter(bytes);
}

export default generateDirectDepositPdf;
